import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useApi } from '../../hooks/useApi';
import { useTheme } from '../../contexts/ThemeContext';
import { IconSun, IconMoon } from '../../components/icons';
import AppStatus from '../layout/AppStatus';
import './AdminLayout.css';

const LAST_VIEWED_KEY = 'adminSectionsLastViewed';

const SECTION_ROUTES = {
    '/admin/users': 'users',
    '/admin/public-profiles': 'users',
    '/admin/support': 'support',
    '/admin/agency-requests': 'agency',
    '/admin/billing': 'billing',
    '/admin/ownership-transfers': 'transfers',
};

function getLastViewed() {
    try { return JSON.parse(localStorage.getItem(LAST_VIEWED_KEY) || '{}'); }
    catch { return {}; }
}

function saveLastViewed(value) {
    try {
        localStorage.setItem(LAST_VIEWED_KEY, JSON.stringify(value));
    } catch {
        // Ignore storage write failures in restricted browser contexts.
    }
}

function Badge({ count }) {
    if (!count) return null;
    return <span className="admin-nav-badge">{count > 99 ? '99+' : count}</span>;
}

function AdminLayout() {
    const { user, logout } = useAuth();
    const { request, status } = useApi();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [counts, setCounts] = useState({ support: 0, agency: 0, users: 0, billing: 0, transfers: 0 });

    useEffect(() => {
        const section = SECTION_ROUTES[location.pathname];
        if (section) {
            const lastViewed = getLastViewed();
            lastViewed[section] = new Date().toISOString();
            saveLastViewed(lastViewed);
            setCounts(prev => ({ ...prev, [section]: 0 }));
        }
    }, [location.pathname]);

    useEffect(() => {
        if (!user || (user.role !== 'admin' && user.email !== 'admin@энилекс.рф')) {
            return;
        }

        const fetchCounts = () => {
            const lastViewed = getLastViewed();
            const params = new URLSearchParams();
            if (lastViewed.support) params.set('since_support', lastViewed.support);
            if (lastViewed.agency) params.set('since_agency', lastViewed.agency);
            if (lastViewed.users) params.set('since_users', lastViewed.users);
            if (lastViewed.billing) params.set('since_billing', lastViewed.billing);
            if (lastViewed.transfers) params.set('since_transfers', lastViewed.transfers);

            request(`/api/v1/admin/counts?${params.toString()}`)
                .then(data => setCounts(data))
                .catch(() => {});
        };

        fetchCounts();
        const timer = setInterval(fetchCounts, 60_000);
        return () => clearInterval(timer);
    }, [user]);

    if (!user || (user.role !== 'admin' && user.email !== 'admin@энилекс.рф')) {
        return (
            <div className="admin-forbidden">
                <h2>Доступ запрещен</h2>
                <p>У вас нет прав администратора.</p>
                <button onClick={() => navigate('/')}>Вернуться на главную</button>
            </div>
        );
    }

    const hasPermission = (permission) => {
        if (!user) return false;
        const perms = user.admin_permissions || [];
        return perms.includes('all') || perms.includes(permission) || user.email === 'admin@энилекс.рф';
    };

    return (
        <div className="admin-layout">
            {status && <AppStatus status={status} />}
            <aside className="admin-sidebar">
                <div className="admin-sidebar__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0 }}>Энилекс. Админ</h3>
                    <button
                        onClick={toggleTheme}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'currentColor',
                            opacity: 0.7,
                            transition: 'opacity 0.2s',
                        }}
                        onMouseEnter={(e) => e.target.style.opacity = '1'}
                        onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                        title={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
                    >
                        {theme === 'dark' ? (
                            <IconSun style={{ width: '20px', height: '20px' }} />
                        ) : (
                            <IconMoon style={{ width: '20px', height: '20px' }} />
                        )}
                    </button>
                </div>
                <nav className="admin-sidebar__nav">
                    {hasPermission('dashboard.view') && (
                        <NavLink to="/admin/dashboard" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Дашборд
                        </NavLink>
                    )}
                    {hasPermission('devops.view') && (
                        <NavLink to="/admin/ops" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Обслуживание
                        </NavLink>
                    )}
                    {/* AI */}
                    {(hasPermission('seo.view') || hasPermission('system.logs') || hasPermission('system.settings')) && (
                        <div className="admin-sidebar__divider">AI</div>
                    )}
                    {hasPermission('system.logs') && (
                        <NavLink to="/admin/ai-logs" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Логи AI-агента
                        </NavLink>
                    )}
                    {hasPermission('system.logs') && (
                        <NavLink to="/admin/ai-chat-shares" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Публичные ссылки чатов
                        </NavLink>
                    )}
                    {hasPermission('system.logs') && (
                        <NavLink to="/admin/ai-satisfaction" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Удовлетворённость чатом
                        </NavLink>
                    )}
                    {hasPermission('system.settings') && (
                        <NavLink to="/admin/jev" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Jev
                        </NavLink>
                    )}
                    {/* Пользователи и компании */}
                    {(hasPermission('users.view') || hasPermission('support.view') || hasPermission('agency.manage') || hasPermission('resources.manage')) && (
                        <div className="admin-sidebar__divider">Пользователи и компании</div>
                    )}
                    {hasPermission('users.view') && (
                        <>
                            <NavLink to="/admin/users" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                                Пользователи
                                <Badge count={counts.users} />
                            </NavLink>
                            <NavLink to="/admin/public-profiles" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                                Публичные профили
                            </NavLink>
                        </>
                    )}
                    {hasPermission('support.view') && (
                        <NavLink to="/admin/support" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Техподдержка
                            <Badge count={counts.support} />
                        </NavLink>
                    )}
                    {hasPermission('agency.manage') && (
                        <NavLink to="/admin/agency-requests" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Заявки «Агентство»
                            <Badge count={counts.agency} />
                        </NavLink>
                    )}
                    {hasPermission('resources.manage') && (
                        <>
                            <NavLink to="/admin/workspaces" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                                Проекты
                            </NavLink>
                            <NavLink to="/admin/marketplaces" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                                Кабинеты
                            </NavLink>
                        </>
                    )}
                    {hasPermission('billing.view') && (
                        <NavLink to="/admin/ownership-transfers" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Передача прав
                            <Badge count={counts.transfers} />
                        </NavLink>
                    )}

                    {/* Оплаты */}
                    {(hasPermission('billing.view') || hasPermission('referrals.view')) && (
                        <div className="admin-sidebar__divider">Оплаты</div>
                    )}
                    {hasPermission('billing.view') && (
                        <>
                            <NavLink to="/admin/billing" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                                Транзакции
                                <Badge count={counts.billing} />
                            </NavLink>
                            <NavLink to="/admin/marketplace-accounts" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                                Подписки
                            </NavLink>
                        </>
                    )}
                    {hasPermission('referrals.view') && (
                        <NavLink to="/admin/referrals" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Рефералы
                        </NavLink>
                    )}
                    {hasPermission('billing.edit') && (
                        <NavLink to="/admin/promocodes" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Промокоды
                        </NavLink>
                    )}
                    {hasPermission('billing.edit') && (
                        <NavLink to="/admin/plan-definitions" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Конструктор тарифов
                        </NavLink>
                    )}
                    {hasPermission('billing.edit') && (
                        <NavLink to="/admin/tool-registry" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Реестр инструментов
                        </NavLink>
                    )}
                    {hasPermission('billing.edit') && (
                        <NavLink to="/admin/tool-manual" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Мануал инструментов
                        </NavLink>
                    )}

                    {/* Система */}
                    {(hasPermission('system.settings') || hasPermission('system.jobs') || hasPermission('system.logs') || hasPermission('monitoring.view')) && (
                        <div className="admin-sidebar__divider">Система</div>
                    )}
                    {hasPermission('system.jobs') && (
                        <NavLink to="/admin/jobs" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Фоновые задачи
                        </NavLink>
                    )}
                    {hasPermission('system.settings') && (
                        <NavLink to="/admin/parser-hub" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Хаб парсинга
                        </NavLink>
                    )}
                    {hasPermission('billing.view') && (
                        <NavLink to="/admin/api-tokens" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            API токены
                        </NavLink>
                    )}
                    {hasPermission('system.logs') && (
                        <NavLink to="/admin/logs" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Логи аудита
                        </NavLink>
                    )}
                    {hasPermission('monitoring.view') && (
                        <a
                            href="http://72.56.248.27:3000"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="admin-nav-item"
                            title="Открыть Grafana мониторинг в новой вкладке"
                        >
                            Мониторинг (Grafana) ↗
                        </a>
                    )}

                    {/* Маркетинг */}
                    {(hasPermission('system.settings') || hasPermission('notifications.manage')) && (
                        <div className="admin-sidebar__divider">Маркетинг</div>
                    )}
                    {hasPermission('system.settings') && (
                        <NavLink to="/admin/marketing/utm" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            UTM-генератор
                        </NavLink>
                    )}
                    {hasPermission('system.settings') && (
                        <NavLink to="/admin/marketing/sources" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Источники
                        </NavLink>
                    )}
                    {hasPermission('system.settings') && (
                        <NavLink to="/admin/marketing/integrations" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Интеграции аналитики
                        </NavLink>
                    )}
                    {hasPermission('system.settings') && (
                        <NavLink to="/admin/marketing/content-agent" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Генерация статей
                        </NavLink>
                    )}
                    {hasPermission('system.settings') && (
                        <NavLink to="/admin/blog" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Блог CMS
                        </NavLink>
                    )}
                    {hasPermission('notifications.manage') && (
                        <NavLink to="/admin/notifications" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Уведомления
                        </NavLink>
                    )}
                    {hasPermission('system.settings') && (
                        <NavLink to="/admin/notification-catalog" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Каталог уведомлений
                        </NavLink>
                    )}

                    {/* Настройки */}
                    {hasPermission('system.settings') && (
                        <div className="admin-sidebar__divider">Настройки</div>
                    )}
                    {hasPermission('system.settings') && (
                        <NavLink to="/admin/settings" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}>
                            Настройки
                        </NavLink>
                    )}
                </nav>
                <div className="admin-sidebar__footer">
                    <button onClick={() => navigate('/')} className="admin-nav-item">
                        В приложение
                    </button>
                    <button onClick={logout} className="admin-nav-item logout">
                        Выйти
                    </button>
                </div>
            </aside>
            <main className="admin-content">
                <Outlet />
            </main>
        </div>
    );
}

export default AdminLayout;
